import React from "react";
import { differenceInWeeks, differenceInDays } from "date-fns";
import { getFrenchWeekString, addWeeks, addWeeksAndFormat, addWeeksDaysAndFormat, formatDate } from "./dateUtils"
import { CircularProgressbar } from 'react-circular-progressbar';
import Cycle from "./Cycle";
import descriptions from "./weeklyDescriptions";
import DateLine from "./DateLine";
import DateOption from "./DateOption";
import InputDate from "./InputDate";
import Conges from "./Conges";

// What the date field is actually asking for, per base-of-calculation. The field
// used to carry only an sr-only "Date" label, so on screen it was an unlabelled
// jj/mm/aaaa box — the one users clicked repeatedly without filling in.
const DATE_FIELD_COPY = {
  lastPeriod: {
    label: "Date de vos dernières règles",
    hint: "Le 1er jour de vos dernières règles. C'est la méthode de calcul standard.",
  },
  startPregnancy: {
    label: "Date de début de grossesse",
    hint: "La date de conception, si vous la connaissez.",
  },
  endPregnancy: {
    label: "Date prévue d'accouchement",
    hint: "Le terme déjà estimé par votre médecin ou votre échographie de datation.",
  },
};

// Mirrors the min/max on the date input. Typing a year directly bypasses those
// attributes, and an out-of-range one produced output like "105729ème semaine".
const MIN_YEAR = 2000;
const MAX_YEAR = 2035;

function isPlausiblePregnancyDate(date) {
  const year = date.getFullYear();
  return year >= MIN_YEAR && year <= MAX_YEAR;
}

export default function Grossesse() {
  const [inputDate, setInputDate] = React.useState()
  const [baseDate, setBaseDate] = React.useState(undefined)
  const [dateOption, setDateOption] = React.useState("startPregnancy");
  const [percentual, setPercentual] = React.useState(0)
  const [week, setWeek] = React.useState(0)
  const [description, setDescription] = React.useState()

  function handleInputDateChange(e) {
    setInputDate(e.target.value);
  }

  function handleDateOptionChange(e) {
    setDateOption(e.target.value);
  }

  React.useEffect(() => {
    let parsedInputDate = new Date(inputDate);
    // Clearing the field, or a year outside the plausible range, must reset the
    // results. This used to `return` early and leave the previous calendar on
    // screen, so an emptied date field still showed a full set of stale dates.
    if (isNaN(parsedInputDate.getTime()) || !isPlausiblePregnancyDate(parsedInputDate)) {
      setBaseDate(undefined);
      return;
    }

    if (dateOption === "lastPeriod") {
      setBaseDate(addWeeks(parsedInputDate, 2));
    } else if (dateOption === "startPregnancy") {
      setBaseDate(parsedInputDate);
    } else if (dateOption == "endPregnancy") {
      setBaseDate(addWeeks(parsedInputDate, -39));
    }
  }, [inputDate, dateOption]);

  React.useEffect(() => {
    if (baseDate) {
      const today = new Date();
      const days = differenceInDays(today, new Date(baseDate));
      // Clamped: a date outside the pregnancy window otherwise drives the progress
      // ring to a negative or >100 value, which renders as a broken arc.
      const rawPercentual = (days / (39 * 7)) * 100
      setPercentual(Math.min(100, Math.max(0, rawPercentual)).toFixed(0))
      const weeks = differenceInWeeks(today, new Date(baseDate));
      setWeek(weeks);
      setDescription(descriptions[weeks]);
    }
  }, [baseDate]);

  const fieldCopy = DATE_FIELD_COPY[dateOption] || DATE_FIELD_COPY.startPregnancy;
  const hasResults = Boolean(baseDate);

  return (
    <>
      <div className="flex w-full text-center gap-2 sm:gap-4"></div>
      <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg">
        <DateOption dateOption={dateOption} handleDateOptionChange={handleDateOptionChange} />
        <InputDate
          inputDate={inputDate}
          handleInputDateChange={handleInputDateChange}
          label={fieldCopy.label}
          hint={fieldCopy.hint}
        />
      </div>

      {/* Before a date is entered the calculator used to render its full skeleton —
          seven cards of empty dates — which reads as a broken tool rather than as
          one waiting for input. */}
      {!hasResults && (
        <div className="p-6 my-2 sm:my-4 bg-stone-50 border border-dashed border-stone-300 rounded-lg text-center">
          <p className="text-gray-900 font-semibold text-sm sm:text-base">
            Renseignez une date pour afficher votre calendrier
          </p>
          <p className="text-gray-600 text-sm mt-2">
            Vous obtiendrez votre date d'accouchement, votre semaine de grossesse, et les
            dates de vos échographies, consultations, dépistages et congé maternité.
          </p>
        </div>
      )}

      {hasResults && (
      <>
      <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">
        Résultats
      </div>

      <div className="p-4 my-2 sm:my-4 bg-lime-100 rounded-lg">
        <div className="w-20 sm:w-24 mx-auto mt-2 mb-4 sm:mb-6">
          <CircularProgressbar text={`${percentual}%`} value={percentual} />
        </div>
        <div className="text-gray-900 font-bold text-center text-sm sm:text-base">
          {getFrenchWeekString(week)}
        </div>
      </div>

      <div className="flex items-stretch gap-2 sm:gap-4">
        <div className="w-1/2 bg-sky-700 rounded-lg p-4 shadow-md flex flex-col justify-between">
          <div className="gap-2 items-center flex">
            <div className="text-white">
              <svg
                className="fill-current h-7 w-7"
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 -960 960 960"
                width="24"
                aria-hidden="true"
              >
                <path d="m612-292 56-56-148-148v-184h-80v216l172 172ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-400Zm0 320q133 0 226.5-93.5T800-480q0-133-93.5-226.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160Z" />
              </svg>
            </div>
            <div className="text-xs text-white sm:text-base w-full cursor-default">
              Conception
            </div>
          </div>
          <div className="text-xl w-full text-center font-bold text-white h-full flex items-center">
            <label id="estimated-conception-label" className="sr-only">
              Conception estimé:
            </label>
            <span
              id="estimated-conception-date"
              className="self-center w-full"
              aria-labelledby="estimated-conception-label"
            >
              {addWeeksAndFormat(baseDate, 0)}
            </span>
          </div>
        </div>

        <div className="w-1/2 bg-indigo-900 rounded-lg p-4 shadow-md flex flex-col justify-between">
          <div className="gap-2 flex items-center">
            <div className="text-white">
              <svg
                className="w-8 h-8"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                color="#ffffff"
                fill="none"
              >
                <circle cx="12" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M16.874 12C17.5826 13.037 18 14.3093 18 15.6842C18 16.5017 17.8524 17.2829 17.5838 18M7.12605 12C6.41738 13.037 6 14.3093 6 15.6842C6 19.1723 8.68629 22 12 22C14.5371 22 16.7064 20.3424 17.5838 18M17.5838 18C14.8509 16.8 12.0559 14.8333 11 14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M12.0002 2C11.0061 2 10.2002 2.7835 10.2002 3.75C10.2002 4.7165 11.0061 5.5 12.0002 5.5C12.4612 5.5 12.8817 5.3315 13.2002 5.0544"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M10 8H10.0081M14 8H14.0081"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="text-sm text-white sm:text-base w-full cursor-default">
              Terme estimé
            </div>
          </div>
          <div className="w-full text-center text-white h-full flex items-center my-2">
            <div className="flex flex-col self-center w-full">
              <label id="estimated-term-label" className="sr-only">
                Terme estimé:
              </label>
              <span
                id="estimated-term-date"
                className="font-bold text-xl"
                aria-labelledby="estimated-term-label"
              >
                {addWeeksAndFormat(baseDate, 39)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 my-2 sm:my-4 bg-slate-600 rounded-lg">
        <div className="flex items-center mb-4 gap-2">
          <svg
            className="text-white w-6 h-6"
            data-slot="icon"
            aria-hidden="true"
            fill="none"
            strokeWidth="1.5"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
          </svg>
          <div className=" text-white font-bold">Calendrier général</div>
        </div>
        <div className="flex flex-col">
          <DateLine description="Dernières règles" date={addWeeksAndFormat(baseDate, -2)} />
          <DateLine
            description="Début de grossesse"
            date={formatDate(baseDate)}
          />
          <DateLine
            description="Déclaration de la grossesse"
            before={true}
            date={addWeeksAndFormat(baseDate, 13)}
          />
        </div>
      </div>

      <div className="p-4 my-2 sm:my-4 bg-stone-700 rounded-lg">
        <div className="flex items-center mb-4 gap-2">
          <svg
            className="fill-current h-6 w-6 text-white"
            xmlns="http://www.w3.org/2000/svg"
            height="24"
            viewBox="0 -960 960 960"
            width="24"
            aria-hidden="true"
          >
            <path d="M480-100q-133 0-226.5-92T160-416q0-63 24.5-120.5T254-638l226-222 226 222q45 44 69.5 101.5T800-416q0 132-93.5 224T480-100Z" />
          </svg>
          <div className=" text-white font-bold">Depistages</div>
        </div>
        <DateLine
          description="HT21 (Trimestre 1)"
          date={addWeeksAndFormat(baseDate, 9)}
          endDate={addWeeksDaysAndFormat(baseDate, 11, 6)}
        />
        <DateLine
          description="HT21 (Trimestre 2)"
          date={addWeeksAndFormat(baseDate, 12)}
          endDate={addWeeksAndFormat(baseDate, 16)}
        />
        <DateLine
          description="HGPO"
          date={addWeeksAndFormat(baseDate, 22)}
          endDate={addWeeksAndFormat(baseDate, 26)}
        />
        <DateLine
          description="Rhophylac"
          date={addWeeksAndFormat(baseDate, 25)}
          endDate={addWeeksAndFormat(baseDate, 27)}
        />
      </div>

      <div className="p-4 my-2 sm:my-4 bg-fuchsia-950 rounded-lg">
        <div className="flex items-center mb-4 gap-2">
          <svg
            className="text-white w-6 h-6"
            data-slot="icon"
            aria-hidden="true"
            fill="#fff"
            strokeWidth="2"
            stroke="currentColor"
            viewBox="-25.6 -51.2 319.99999 319.99999"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g id="g5" transform="translate(-120.61778,-137.11781)">
              <path
                d="m 300.9038,174.4087 -9.335,-9.0469 a 49.5308,49.5308 0 0 1 -71.1376,0 l -9.335,9.0469 a 62.5293,62.5293 0 0 0 89.8076,0 z"
                id="path3"
              />
              <path
                d="m 110.8994,275.7407 a 203.7822,203.7822 0 0 0 290.2017,0 8.2063,8.2063 0 0 0 -0.112,-11.6271 c -0.009,-0.009 -0.0166,-0.0188 -0.0257,-0.0277 l -10e-4,-9e-4 v 0 0 L 318.788,184.4527 a 8.2579,8.2579 0 0 0 -11.4941,0.0752 73.15,73.15 0 0 1 -102.5855,0.001 8.2575,8.2575 0 0 0 -11.4966,-0.0762 l -82.1737,79.6309 v 0 0 l -0.001,9e-4 c -0.009,0.009 -0.0166,0.0188 -0.0257,0.0277 a 8.2063,8.2063 0 0 0 -0.1115,11.6276 z m 281.0186,-2.3178 -6e-4,-7e-4 7e-4,5e-4 z M 198.9766,196.9683 a 86.2527,86.2527 0 0 0 114.0468,0 l 44.5289,43.152 c -26.82,22.8533 -57.8325,36.242 -90.09,38.82 l 1.0352,12.9589 c 35.3943,-2.8287 69.3,-17.5656 98.4251,-42.698 l 21.4815,20.8174 a 190.7922,190.7922 0 0 1 -264.8087,0 l 36.05,-34.9349 c 16.5158,15.3185 36.1461,24.7287 62.7451,30.164 l 2.6026,-12.7363 c -24.1743,-4.94 -41.3053,-12.9991 -56.0016,-26.4847 z m -78.8931,76.4526 0.001,-0.001 -0.003,0.003 v 0 z"
                id="path4"
              />
            </g>
          </svg>
          <div className=" text-white font-bold">Échographies</div>
        </div>
        <div className="flex flex-col">
          <DateLine
            description="1re échographie"
            date={addWeeksDaysAndFormat(baseDate, 8, 1)}
            endDate={addWeeksAndFormat(baseDate, 12)}
          />
          <DateLine
            description="2eme échographie"
            date={addWeeksDaysAndFormat(baseDate, 17, 1)}
            endDate={addWeeksAndFormat(baseDate, 23)}
          />
          <DateLine
            description="3eme échographie"
            date={addWeeksDaysAndFormat(baseDate, 27, 1)}
            endDate={addWeeksAndFormat(baseDate, 33)}
          />
        </div>
      </div>

      <div className="p-4 my-2 sm:my-4 bg-cyan-800 rounded-lg">
        <div className="flex items-center mb-4 gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            color="#ffffff"
            fill="none"
          >
            <path
              d="M6.5 3.5H4.5C3.94772 3.5 3.5 3.94772 3.5 4.5V8C3.5 11.0376 5.96243 13.5 9 13.5C12.0376 13.5 14.5 11.0376 14.5 8V4.5C14.5 3.94772 14.0523 3.5 13.5 3.5H11.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.5 15.5V16.75C18.5 19.3734 16.3734 21.5 13.75 21.5C11.1266 21.5 9 19.3734 9 16.75V13.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11.5 2.5V4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.5 2.5V4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20.5 13.5C20.5 14.6046 19.6046 15.5 18.5 15.5C17.3954 15.5 16.5 14.6046 16.5 13.5C16.5 12.3954 17.3954 11.5 18.5 11.5C19.6046 11.5 20.5 12.3954 20.5 13.5Z"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
          <div className=" text-white font-bold">
            Consultations prénatales
          </div>
        </div>
        <div className="flex flex-col">
          <DateLine description="1ère consultation" before={true} date={addWeeksAndFormat(baseDate, 8)} />
          <DateLine description="2ème consultation" before={true} date={addWeeksAndFormat(baseDate, 13)} />
          <DateLine description="3ème consultation" before={true} date={addWeeksAndFormat(baseDate, 17)} />
          <DateLine description="4ème consultation" before={true} date={addWeeksAndFormat(baseDate, 21)} />
          <DateLine description="5ème consultation" before={true} date={addWeeksAndFormat(baseDate, 26)} />
          <DateLine description="6ème consultation" before={true} date={addWeeksAndFormat(baseDate, 30)} />
          <DateLine description="7ème consultation" before={true} date={addWeeksAndFormat(baseDate, 35)} />
          <DateLine description="8ème consultation" before={true} date={addWeeksAndFormat(baseDate, 38)} />
        </div>
      </div>

      <div className="p-4 my-2 sm:my-4 bg-teal-700 rounded-lg">
        <div className="flex items-center mb-4 gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="#fff" fill="none">
            <path d="M13.1418 14.4418C14.3486 13.7108 15.4018 14.0054 16.0345 14.4747C16.294 14.6671 16.4237 14.7633 16.5 14.7633C16.5763 14.7633 16.706 14.6671 16.9655 14.4747C17.5982 14.0054 18.6514 13.7108 19.8582 14.4418C21.4419 15.4013 21.8002 18.5666 18.1472 21.237C17.4514 21.7457 17.1035 22 16.5 22C15.8965 22 15.5486 21.7457 14.8528 21.237C11.1998 18.5666 11.5581 15.4013 13.1418 14.4418Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M3 8H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18 2V4M6 2V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M11.05 22C7.01949 22 5.00424 22 3.75212 20.6464C2.5 19.2927 2.5 17.1141 2.5 12.7568V12.2432C2.5 7.88594 2.5 5.70728 3.75212 4.35364C5.00424 3 7.01949 3 11.05 3H12.95C16.9805 3 18.9958 3 20.2479 4.35364C21.4765 5.68186 21.4996 7.80438 21.5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className=" text-white font-bold">
            Congé Maternité
          </div>
        </div>
        <div className="flex flex-col">
          <Conges baseDate={baseDate} />
        </div>
      </div>
      </>
      )}
    </>
  );
}